/**
 * Modal de publicação de vaga no LinkedIn.
 *
 * Fluxo:
 * 1. Abre, busca preview + accounts do user via GET /jobs/{id}/linkedin/preview
 * 2. Mostra:
 *    - Dropdown "Publicar como:" com as contas do user (avatar + nome + status)
 *    - Textarea com preview editável (recruiter pode tweakar)
 *    - Indicador de caracteres restantes (LinkedIn corta em ~1300 no feed)
 * 3. Botão "Publicar agora" — chama POST /jobs/{id}/linkedin/publish
 * 4. Sucesso: toast + onPublished com {post_url} pra parent atualizar a UI
 *
 * Bloqueios:
 * - Sem contas conectadas → CTA pra ir em /integracoes/linkedin
 * - Conta não ready (status != connected) → desabilitada no dropdown
 * - Já publicada → modal mostra link e oferece pra abrir o post existente
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Linkedin,
  Loader2,
  ExternalLink,
  Send,
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MAX_CHARS = 2800; // hard cap nosso
const SOFT_CAP = 1300; // a partir daqui o LinkedIn corta com "see more"

/**
 * Converte um ISO UTC pra string "YYYY-MM-DDTHH:MM" no fuso BRT (UTC-3),
 * formato aceito pelo <input type="datetime-local">.
 */
const isoUtcToBrtLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "";
    // BRT é UTC-3 → subtrai 3h do UTC
    const brt = new Date(dt.getTime() - 3 * 60 * 60 * 1000);
    const yyyy = brt.getUTCFullYear();
    const mm = String(brt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(brt.getUTCDate()).padStart(2, "0");
    const hh = String(brt.getUTCHours()).padStart(2, "0");
    const mi = String(brt.getUTCMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return "";
  }
};

/**
 * Lê o valor de <input type="datetime-local"> (interpretado como BRT pela
 * UX) e devolve ISO UTC. Ex: "2026-06-01T10:00" → "2026-06-01T13:00:00Z".
 */
const brtLocalInputToIsoUtc = (local) => {
  if (!local) return "";
  try {
    // Tratamos como BRT: adicionamos +3h pra obter UTC
    const [datePart, timePart] = local.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mi] = (timePart || "00:00").split(":").map(Number);
    // Cria com UTC e adiciona 3h
    const dt = new Date(Date.UTC(y, m - 1, d, hh + 3, mi, 0));
    return dt.toISOString();
  } catch {
    return "";
  }
};

const fmtBRDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};


const JobLinkedInPublishDialog = ({ open, onClose, job, onPublished }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState({
    preview_text: "",
    accounts: [],
    already_published: false,
    existing_post_url: "",
    suggested_scheduled_at: "",
    schedule_status: "",
    scheduled_at: "",
    scheduled_by: "",
    schedule_error: "",
    smart_queue: {
      suggested_hour_brt: null,
      suggested_date_brt: "",
      conflicts_on_this_date: [],
      day_is_full: false,
      alternatives: [],
    },
  });
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [text, setText] = useState("");
  // "now" | "schedule"
  const [mode, setMode] = useState("now");
  // Valor do <input type="datetime-local"> em BRT
  const [scheduledLocal, setScheduledLocal] = useState("");

  useEffect(() => {
    if (!open || !job?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/${job.id}/linkedin/preview`);
        if (cancelled) return;
        setPreview(res.data);
        setText(res.data.preview_text || "");
        const firstReady = (res.data.accounts || []).find((a) => a.ready);
        setSelectedAccountId(firstReady?.id || "");
        // Pré-preenche o input com a sugestão (já no formato BRT)
        setScheduledLocal(isoUtcToBrtLocalInput(res.data.suggested_scheduled_at));
        // Se já tem agendamento pendente, abre direto na aba "schedule"
        if (res.data.schedule_status === "pending") {
          setMode("schedule");
          setScheduledLocal(isoUtcToBrtLocalInput(res.data.scheduled_at));
        } else {
          setMode("now");
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || "Erro ao carregar preview do LinkedIn",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, job?.id]);

  // Recalcula a sugestão de slot quando o usuário troca de conta no modal.
  // (Cada conta LinkedIn tem sua própria fila de agendamentos.)
  useEffect(() => {
    if (!open || !selectedAccountId || preview.schedule_status === "pending") {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/integrations/linkedin/accounts/${selectedAccountId}/next-slot`,
        );
        if (cancelled) return;
        setPreview((p) => ({
          ...p,
          suggested_scheduled_at: res.data.scheduled_at_iso,
          smart_queue: {
            suggested_hour_brt: res.data.hour_brt,
            suggested_date_brt: res.data.date_brt_iso,
            conflicts_on_this_date: res.data.conflicts_on_this_date,
            day_is_full: res.data.day_is_full,
            alternatives: res.data.alternatives,
          },
        }));
        // Atualiza o input só se o user ainda não tocou (igual ao sugerido anterior)
        setScheduledLocal((current) =>
          current === isoUtcToBrtLocalInput(preview.suggested_scheduled_at)
            ? isoUtcToBrtLocalInput(res.data.scheduled_at_iso)
            : current,
        );
      } catch {
        // silent — fallback usa o que já tem
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const handlePublish = async () => {
    if (!selectedAccountId) {
      toast.error("Selecione uma conta LinkedIn pra publicar.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/linkedin/publish`, {
        linkedin_account_id: selectedAccountId,
        custom_text: text,
      });
      toast.success("Vaga publicada no LinkedIn com sucesso! 🚀");
      onPublished?.(res.data);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao publicar no LinkedIn",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedAccountId) {
      toast.error("Selecione uma conta LinkedIn pra agendar.");
      return;
    }
    const isoUtc = brtLocalInputToIsoUtc(scheduledLocal);
    if (!isoUtc) {
      toast.error("Data inválida.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/jobs/${job.id}/linkedin/schedule`,
        {
          linkedin_account_id: selectedAccountId,
          scheduled_at: isoUtc,
          custom_text: text,
        },
      );
      toast.success(
        `Publicação agendada para ${fmtBRDateTime(res.data.scheduled_at)} ⏰`,
      );
      onPublished?.(res.data);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao agendar publicação no LinkedIn",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSchedule = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/jobs/${job.id}/linkedin/schedule`);
      toast.success("Agendamento cancelado.");
      onPublished?.({ cancelled: true });
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao cancelar agendamento",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseSuggestion = () => {
    setScheduledLocal(isoUtcToBrtLocalInput(preview.suggested_scheduled_at));
  };

  const charCount = text.length;
  const overSoftCap = charCount > SOFT_CAP;
  const overHardCap = charCount > MAX_CHARS;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="linkedin-publish-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Linkedin className="w-4 h-4 text-white" />
            </div>
            Publicar vaga no LinkedIn
          </DialogTitle>
          <DialogDescription>
            Confira o texto, escolha a conta e publique. O post aparecerá no
            feed da conta corporativa selecionada.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
            Carregando preview…
          </div>
        ) : preview.already_published ? (
          // Vaga já publicada — bloqueio
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900 leading-relaxed flex-1">
                <strong>Esta vaga já está publicada no LinkedIn.</strong>
                <span className="block mt-1">
                  Pra republicar, você precisa primeiro encerrar a publicação
                  existente. Pra ver o post atual, clique no botão abaixo.
                </span>
              </div>
            </div>
            {preview.existing_post_url && (
              <a
                href={preview.existing_post_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-existing-post-link"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline font-medium"
              >
                <ExternalLink className="w-4 h-4" /> Abrir post atual no LinkedIn
              </a>
            )}
          </div>
        ) : preview.accounts.length === 0 ? (
          // Sem contas conectadas
          <div className="space-y-3 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900 leading-relaxed">
              <strong>
                Você ainda não tem uma conta LinkedIn conectada.
              </strong>
              <span className="block mt-1">
                Pra publicar vagas, conecte primeiro sua conta corporativa no
                menu <strong>LinkedIn</strong>.
              </span>
            </div>
            <Button
              onClick={() => {
                onClose();
                window.location.href = "/integracoes/linkedin";
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="goto-linkedin-page-button"
            >
              <Linkedin className="w-4 h-4 mr-1.5" /> Ir conectar conta
            </Button>
          </div>
        ) : (
          // Fluxo normal
          <div className="space-y-4">
            {/* Banner "já agendada" quando schedule_status === pending */}
            {preview.schedule_status === "pending" && (
              <div
                data-testid="linkedin-schedule-pending-banner"
                className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-md p-3 flex items-start gap-2.5"
              >
                <CalendarClock className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-violet-900">
                  <strong>Publicação já agendada</strong> para{" "}
                  <strong>{fmtBRDateTime(preview.scheduled_at)}</strong>
                  {preview.scheduled_by && (
                    <span> por {preview.scheduled_by}</span>
                  )}
                  <span className="block text-xs opacity-80 mt-0.5">
                    Você pode reagendar ajustando a data abaixo ou cancelar.
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelSchedule}
                  disabled={submitting}
                  data-testid="cancel-linkedin-schedule-button"
                  className="border-violet-300 text-violet-700 hover:bg-violet-100 shrink-0"
                >
                  Cancelar agendamento
                </Button>
              </div>
            )}

            {preview.schedule_status === "failed" && (
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3 flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-rose-900">
                  <strong>Tentativa anterior de publicação agendada falhou.</strong>
                  {preview.schedule_error && (
                    <span className="block text-xs opacity-90 mt-0.5">
                      Motivo: {preview.schedule_error}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Toggle modo (Publicar agora vs Agendar) */}
            <div
              data-testid="linkedin-mode-toggle"
              className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-lg"
            >
              <button
                type="button"
                onClick={() => setMode("now")}
                data-testid="mode-now"
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  mode === "now"
                    ? "bg-white shadow text-blue-700"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Send className="w-4 h-4" />
                Publicar agora
              </button>
              <button
                type="button"
                onClick={() => setMode("schedule")}
                data-testid="mode-schedule"
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  mode === "schedule"
                    ? "bg-white shadow text-violet-700"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <CalendarClock className="w-4 h-4" />
                Agendar
              </button>
            </div>

            {/* Picker de data quando modo schedule */}
            {mode === "schedule" && (
              <div className="space-y-1.5">
                <Label htmlFor="linkedin-scheduled-at" className="text-sm font-medium">
                  Data e hora da publicação (horário de Brasília)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    id="linkedin-scheduled-at"
                    type="datetime-local"
                    data-testid="linkedin-scheduled-at-input"
                    value={scheduledLocal}
                    onChange={(e) => setScheduledLocal(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleUseSuggestion}
                    data-testid="use-suggested-time-button"
                    className="border-violet-300 text-violet-700 hover:bg-violet-50 shrink-0"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Usar sugestão
                  </Button>
                </div>

                {/* Banner da Fila Inteligente */}
                {preview.smart_queue?.conflicts_on_this_date?.length > 0 ? (
                  <div
                    data-testid="smart-queue-banner"
                    className={`rounded-md p-3 text-xs border ${
                      preview.smart_queue.day_is_full
                        ? "bg-amber-50 border-amber-300 text-amber-900"
                        : "bg-violet-50 border-violet-200 text-violet-900"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="flex-1 leading-relaxed">
                        <strong>📅 Fila inteligente:</strong>{" "}
                        {preview.smart_queue.day_is_full ? (
                          <>
                            essa data já tem <strong>3 vagas agendadas</strong> nesta
                            conta — o LinkedIn pode penalizar publicações em sequência.
                            Recomendamos pular pra <strong>{fmtBRDateTime(preview.suggested_scheduled_at)}</strong>.
                          </>
                        ) : (
                          <>
                            essa data já tem <strong>
                              {preview.smart_queue.conflicts_on_this_date.length} vaga
                              {preview.smart_queue.conflicts_on_this_date.length > 1 ? "s" : ""}
                            </strong> agendada{preview.smart_queue.conflicts_on_this_date.length > 1 ? "s" : ""}.
                            Pra maximizar alcance individual, sugerimos o slot livre das{" "}
                            <strong>{preview.smart_queue.suggested_hour_brt}h BRT</strong>.
                          </>
                        )}
                        <ul className="mt-1.5 space-y-0.5 opacity-80">
                          {preview.smart_queue.conflicts_on_this_date.slice(0, 3).map(
                            (c) => (
                              <li key={c.job_id}>
                                • {fmtBRDateTime(c.scheduled_at)} — <em>{c.title || "Sem título"}</em>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 leading-snug">
                    💡 <strong>Sugestão automática:</strong>{" "}
                    {fmtBRDateTime(preview.suggested_scheduled_at)} — próximo dia útil
                    às {preview.smart_queue?.suggested_hour_brt || 10}h (horário de pico de
                    engagement no LinkedIn).
                  </p>
                )}
              </div>
            )}

            {/* Dropdown de contas */}
            <div className="space-y-1.5">
              <Label htmlFor="linkedin-account-select" className="text-sm font-medium">
                Publicar como:
              </Label>
              <div className="grid gap-2" data-testid="linkedin-accounts-radio-list">
                {preview.accounts.map((a) => (
                  <label
                    key={a.id}
                    data-testid={`linkedin-account-option-${a.id}`}
                    className={`flex items-center gap-3 p-2.5 rounded-md cursor-pointer border transition-all ${
                      selectedAccountId === a.id
                        ? "bg-blue-50 border-blue-300 shadow-sm"
                        : "bg-white border-slate-200 hover:border-blue-200"
                    } ${!a.ready ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="radio"
                      name="linkedin-account"
                      value={a.id}
                      disabled={!a.ready}
                      checked={selectedAccountId === a.id}
                      onChange={(e) => setSelectedAccountId(e.target.value)}
                      className="w-4 h-4 accent-blue-600"
                    />
                    {a.linkedin_picture_url ? (
                      <img
                        src={a.linkedin_picture_url}
                        alt={a.linkedin_name}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                        {(a.linkedin_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {a.linkedin_name || "Sem nome"}
                      </div>
                      {a.linkedin_headline && (
                        <div className="text-xs text-slate-500 truncate">
                          {a.linkedin_headline}
                        </div>
                      )}
                    </div>
                    {a.ready ? (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0">
                        <CheckCircle2 className="w-3 h-3" /> Ativa
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0">
                        <AlertTriangle className="w-3 h-3" /> {a.status}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Textarea com preview editável */}
            <div className="space-y-1.5">
              <Label htmlFor="linkedin-post-text" className="text-sm font-medium">
                Texto do post (edite se quiser personalizar)
              </Label>
              <Textarea
                id="linkedin-post-text"
                data-testid="linkedin-post-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={14}
                className="font-mono text-xs leading-relaxed resize-none"
                maxLength={MAX_CHARS + 50}
              />
              <div
                className={`text-xs flex justify-between ${
                  overHardCap
                    ? "text-rose-600 font-semibold"
                    : overSoftCap
                      ? "text-amber-600"
                      : "text-slate-500"
                }`}
              >
                <span>
                  {overSoftCap && !overHardCap && (
                    <>⚠️ LinkedIn vai truncar com "see more" após ~{SOFT_CAP} caracteres.</>
                  )}
                  {overHardCap && (
                    <>⛔ Acima do limite máximo ({MAX_CHARS}). Reduza antes de publicar.</>
                  )}
                </span>
                <span className="tabular-nums">
                  {charCount} / {MAX_CHARS}
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="cancel-linkedin-publish-button"
          >
            Cancelar
          </Button>
          {!preview.already_published && preview.accounts.length > 0 && (
            mode === "schedule" ? (
              <Button
                onClick={handleSchedule}
                disabled={
                  submitting ||
                  loading ||
                  !selectedAccountId ||
                  !scheduledLocal ||
                  overHardCap ||
                  !text.trim()
                }
                data-testid="confirm-linkedin-schedule-button"
                className="bg-violet-600 hover:bg-violet-700"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                )}
                {preview.schedule_status === "pending"
                  ? "Reagendar"
                  : "Agendar publicação"}
              </Button>
            ) : (
              <Button
                onClick={handlePublish}
                disabled={
                  submitting ||
                  loading ||
                  !selectedAccountId ||
                  overHardCap ||
                  !text.trim()
                }
                data-testid="confirm-linkedin-publish-button"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1.5" />
                )}
                Publicar agora
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobLinkedInPublishDialog;
